import { crc32c, ProtocolError } from '@spectrabus/protocol';

const AUDIO_BURST_MAGIC = 0x5342_4131;
const AUDIO_BURST_VERSION = 1;
const AUDIO_SYMBOL_AMPLITUDE = 0.45;
const AUDIO_SYNC_AMPLITUDE = 0.24;
const MAX_AUDIO_BURST_SAMPLES = 16_000_000;
const MAX_AUDIO_STREAM_SAMPLES = 16_000_000;
const MIN_PILOT_GAIN = 1e-6;
const DEFAULT_PREAMBLE_CORRELATION = 0.5;
const MAX_COMMON_CARRIER_OFFSET_HZ = 12;

export const AUDIO_BURST_HEADER_BYTES = 16;

export type AudioModulation = 'OFF' | 'PILOT' | 'BPSK' | 'QPSK' | '16QAM';

export interface AudioCarrier {
  readonly index: number;
  readonly bin: number;
  readonly frequencyHz: number;
  readonly snrDb: number;
  readonly modulation: AudioModulation;
  readonly bitsPerSymbol: number;
  readonly enabled: boolean;
}

export interface AudioCarrierPlan {
  readonly sampleRate: number;
  readonly fftSize: number;
  readonly cyclicPrefixSamples: number;
  readonly carriers: readonly AudioCarrier[];
  readonly usableCarrierCount: number;
  readonly source: 'measured' | 'simulated';
}

export interface SynthesisedAudioBurst {
  readonly samples: Float32Array;
  readonly symbolCount: number;
  readonly bitsPerSymbol: number;
  readonly payloadBytes: number;
  readonly durationSeconds: number;
}

export interface DemodulatedAudioBurst {
  readonly payload: Uint8Array;
  readonly symbolCount: number;
  readonly capturedSymbolCount: number;
  readonly bitsPerSymbol: number;
  readonly payloadCrc32c: number;
}

export interface AudioTransmissionOptions {
  /**
   * Applies a controlled common carrier offset to the emitted PCM. This is
   * primarily useful for deterministic channel tests and remains bounded to
   * the acquisition range of the repeated training symbols.
   */
  readonly carrierFrequencyOffsetHz?: number;
}

export interface SynthesisedAudioTransmission {
  readonly samples: Float32Array;
  readonly preambleSamples: number;
  readonly burstSampleOffset: number;
  readonly burstSamples: number;
  readonly symbolCount: number;
  readonly bitsPerSymbol: number;
  readonly payloadBytes: number;
  readonly durationSeconds: number;
  readonly carrierFrequencyOffsetHz: number;
}

export interface AudioTransmissionEstimate {
  readonly payloadBytes: number;
  readonly framedBytes: number;
  readonly bitsPerSymbol: number;
  readonly symbolCount: number;
  readonly samplesPerSymbol: number;
  readonly symbolDurationSeconds: number;
  readonly preambleSamples: number;
  readonly preambleDurationSeconds: number;
  readonly burstSamples: number;
  readonly totalSamples: number;
  readonly durationSeconds: number;
}

export interface AudioSampleWindow {
  readonly captureSamples: number;
  readonly preambleStart: number;
  readonly preambleEnd: number;
  readonly burstStart: number;
  readonly burstEnd: number;
}

export interface AudioReceiveDiagnostics {
  readonly source: 'measured';
  readonly correlation: number;
  readonly gain: number;
  readonly commonCarrierOffsetHz: number;
  readonly commonPhaseRadians: number;
  readonly snrDb: number;
  readonly sampleWindow: AudioSampleWindow;
}

export interface ReceivedAudioTransmission extends DemodulatedAudioBurst {
  readonly diagnostics: AudioReceiveDiagnostics;
}

export interface AudioReceiveOptions {
  readonly minimumCorrelation?: number;
  readonly maximumPreambleSearchSamples?: number;
}

export interface AudioCarrierBand {
  readonly firstBin: number;
  readonly lastBin: number;
  readonly binSpacing: number;
}

// Frequency-division duplex bands on the shared 512-bin grid at 48 kHz.
// Forward (sender to receiver) and return (receiver to sender) occupy
// disjoint bins with a guard band between 8.25 kHz and 9.56 kHz, so each
// side's own emission is out of band at its own microphone by construction
// and no echo canceller is required. The full band remains the historical
// single-direction allocation (bins 22 to 163, roughly 2.1 to 15.3 kHz).
export const FULL_AUDIO_BAND: AudioCarrierBand = Object.freeze({
  firstBin: 22,
  lastBin: 163,
  binSpacing: 3,
});
export const FORWARD_AUDIO_BAND: AudioCarrierBand = Object.freeze({
  firstBin: 22,
  lastBin: 88,
  binSpacing: 3,
});
export const RETURN_AUDIO_BAND: AudioCarrierBand = Object.freeze({
  firstBin: 102,
  lastBin: 162,
  binSpacing: 3,
});

export function audioBandCarrierCapacity(band: AudioCarrierBand): number {
  return Math.floor((band.lastBin - band.firstBin) / band.binSpacing) + 1;
}

/** Crossover between the duplex halves, inside the guard band. */
export const DUPLEX_CROSSOVER_HZ = 8_950;

export interface StreamingFirFilter {
  process(block: Float32Array): Float32Array;
  reset(): void;
}

/**
 * Linear-phase FIR band-split filter for full-duplex listening. The preamble
 * correlator normalises against total window power, so a near-end speaker a
 * few centimetres from the microphone raises the acquisition floor even when
 * its emission is entirely out of band; filtering the capture stream down to
 * the listener's own half restores the frequency-division isolation where it
 * matters most. Windowed sinc with a Blackman window: 129 taps give roughly
 * 70 dB of stopband across the 1.3 kHz guard transition at 48 kHz.
 */
export function createBandSplitFilter(
  kind: 'low-pass' | 'high-pass',
  cutoffHz: number,
  sampleRate: number,
  taps = 129,
): StreamingFirFilter {
  if (!Number.isInteger(taps) || taps < 31 || taps % 2 === 0) {
    throw new ProtocolError('AUDIO_FILTER_BOUNDS', 'FIR tap count must be an odd integer of at least 31.');
  }
  if (!Number.isFinite(cutoffHz) || cutoffHz <= 0 || cutoffHz >= sampleRate / 2) {
    throw new ProtocolError('AUDIO_FILTER_BOUNDS', 'FIR cutoff must sit inside the Nyquist interval.');
  }
  const kernel = new Float64Array(taps);
  const normalisedCutoff = cutoffHz / sampleRate;
  const middle = (taps - 1) / 2;
  let dcGain = 0;
  for (let n = 0; n < taps; n += 1) {
    const k = n - middle;
    const sinc = k === 0
      ? 2 * Math.PI * normalisedCutoff
      : Math.sin(2 * Math.PI * normalisedCutoff * k) / k;
    const blackman =
      0.42 -
      0.5 * Math.cos((2 * Math.PI * n) / (taps - 1)) +
      0.08 * Math.cos((4 * Math.PI * n) / (taps - 1));
    kernel[n] = sinc * blackman;
    dcGain += kernel[n] ?? 0;
  }
  for (let n = 0; n < taps; n += 1) kernel[n] = (kernel[n] ?? 0) / dcGain;
  if (kind === 'high-pass') {
    // Spectral inversion of the unity-DC low-pass prototype.
    for (let n = 0; n < taps; n += 1) kernel[n] = -(kernel[n] ?? 0);
    kernel[middle] = (kernel[middle] ?? 0) + 1;
  }

  let history = new Float32Array(taps - 1);
  return {
    process(block: Float32Array): Float32Array {
      const extended = new Float32Array(history.length + block.length);
      extended.set(history, 0);
      extended.set(block, history.length);
      const output = new Float32Array(block.length);
      for (let index = 0; index < block.length; index += 1) {
        let accumulator = 0;
        const base = index + taps - 1;
        for (let tap = 0; tap < taps; tap += 1) {
          accumulator += (kernel[tap] ?? 0) * (extended[base - tap] ?? 0);
        }
        output[index] = accumulator;
      }
      history = extended.slice(extended.length - (taps - 1));
      return output;
    },
    reset(): void {
      history = new Float32Array(taps - 1);
    },
  };
}

export function allocateCarriers(
  snrByCarrier: readonly number[],
  sampleRate = 48_000,
  source: AudioCarrierPlan['source'] = 'simulated',
  band: AudioCarrierBand = FULL_AUDIO_BAND,
): AudioCarrierPlan {
  const fftSize = 512;
  if (
    !Number.isInteger(band.firstBin) ||
    !Number.isInteger(band.lastBin) ||
    !Number.isInteger(band.binSpacing) ||
    band.firstBin <= 0 ||
    band.binSpacing < 1 ||
    band.lastBin < band.firstBin ||
    band.lastBin >= fftSize / 2
  ) {
    throw new ProtocolError('AUDIO_PLAN_BAND', 'Carrier band bounds are invalid for the OFDM grid.');
  }
  if (snrByCarrier.length > audioBandCarrierCapacity(band)) {
    throw new ProtocolError(
      'AUDIO_PLAN_BAND',
      'Requested carrier count exceeds the restricted band capacity.',
    );
  }
  const { firstBin, binSpacing } = band;
  const carriers = snrByCarrier.map((snrDb, index): AudioCarrier => {
    const bin = firstBin + index * binSpacing;
    const frequencyHz = (bin * sampleRate) / fftSize;
    const isPilot = index % 8 === 0;
    const aboveNyquistGuard = frequencyHz > sampleRate * 0.42;
    let modulation: AudioModulation = 'OFF';
    let bitsPerSymbol = 0;
    if (!aboveNyquistGuard && snrDb >= 5) {
      if (isPilot) {
        modulation = 'PILOT';
      } else if (snrDb >= 24) {
        modulation = '16QAM';
        bitsPerSymbol = 4;
      } else if (snrDb >= 14) {
        modulation = 'QPSK';
        bitsPerSymbol = 2;
      } else {
        modulation = 'BPSK';
        bitsPerSymbol = 1;
      }
    }
    return {
      index,
      bin,
      frequencyHz,
      snrDb,
      modulation,
      bitsPerSymbol,
      enabled: modulation !== 'OFF',
    };
  });
  return {
    sampleRate,
    fftSize,
    cyclicPrefixSamples: 64,
    carriers,
    usableCarrierCount: carriers.filter((carrier) => carrier.enabled).length,
    source,
  };
}

export function generateCalibrationSweep(
  sampleRate = 48_000,
  durationSeconds = 0.8,
): Float32Array {
  if (sampleRate < 32_000 || durationSeconds <= 0 || durationSeconds > 4) {
    throw new ProtocolError('AUDIO_SWEEP_BOUNDS', 'Calibration sweep settings are outside safe bounds.');
  }
  const samples = Math.floor(sampleRate * durationSeconds);
  const output = new Float32Array(samples);
  const startHz = 1_500;
  const endHz = Math.min(15_500, sampleRate * 0.4);
  const ratio = endHz / startHz;
  let phase = 0;
  for (let index = 0; index < samples; index += 1) {
    const progress = index / Math.max(1, samples - 1);
    const frequency = startHz * ratio ** progress;
    phase += (2 * Math.PI * frequency) / sampleRate;
    const window = Math.sin(Math.PI * progress) ** 2;
    output[index] = Math.sin(phase) * window * 0.18;
  }
  return output;
}

function bitsForCarrier(
  bytes: Uint8Array,
  bitOffset: number,
  bitCount: number,
): number {
  let value = 0;
  for (let bit = 0; bit < bitCount; bit += 1) {
    const absolute = bitOffset + bit;
    const byte = bytes[Math.floor(absolute / 8)] ?? 0;
    value = (value << 1) | ((byte >>> (7 - (absolute % 8))) & 1);
  }
  return value;
}

function expectedBitsPerSymbol(modulation: AudioModulation): number {
  switch (modulation) {
    case 'BPSK':
      return 1;
    case 'QPSK':
      return 2;
    case '16QAM':
      return 4;
    case 'OFF':
    case 'PILOT':
      return 0;
  }
}

interface ValidatedAudioPlan {
  readonly active: readonly AudioCarrier[];
  readonly data: readonly AudioCarrier[];
  readonly pilots: readonly AudioCarrier[];
  readonly bitsPerSymbol: number;
  readonly samplesPerSymbol: number;
  readonly amplitudeScale: number;
}

function validatePlan(plan: AudioCarrierPlan): ValidatedAudioPlan {
  if (
    !Number.isInteger(plan.sampleRate) ||
    plan.sampleRate < 8_000 ||
    !Number.isInteger(plan.fftSize) ||
    plan.fftSize < 32 ||
    !Number.isInteger(plan.cyclicPrefixSamples) ||
    plan.cyclicPrefixSamples < 0 ||
    plan.cyclicPrefixSamples > plan.fftSize
  ) {
    throw new ProtocolError('AUDIO_PLAN_INVALID', 'OFDM timing parameters are invalid.');
  }

  const active = plan.carriers.filter((carrier) => carrier.enabled);
  const pilots = active.filter((carrier) => carrier.modulation === 'PILOT');
  const data = active.filter((carrier) => carrier.modulation !== 'PILOT');
  const occupiedBins = new Set<number>();
  for (const carrier of active) {
    if (
      !Number.isInteger(carrier.bin) ||
      carrier.bin <= 0 ||
      carrier.bin >= plan.fftSize / 2 ||
      occupiedBins.has(carrier.bin) ||
      carrier.modulation === 'OFF' ||
      carrier.bitsPerSymbol !== expectedBitsPerSymbol(carrier.modulation)
    ) {
      throw new ProtocolError('AUDIO_PLAN_INVALID', 'OFDM carrier metadata is inconsistent.');
    }
    occupiedBins.add(carrier.bin);
  }

  const bitsPerSymbol = data.reduce(
    (total, carrier) => total + carrier.bitsPerSymbol,
    0,
  );
  if (pilots.length === 0 || bitsPerSymbol === 0) {
    throw new ProtocolError(
      'AUDIO_PLAN_CAPACITY',
      'An OFDM burst requires at least one pilot and one payload carrier.',
    );
  }

  return {
    active,
    data,
    pilots,
    bitsPerSymbol,
    samplesPerSymbol: plan.fftSize + plan.cyclicPrefixSamples,
    amplitudeScale: AUDIO_SYMBOL_AMPLITUDE / active.length,
  };
}

export function audioBitsPerSymbol(plan: AudioCarrierPlan): number {
  return validatePlan(plan).bitsPerSymbol;
}

export function constellation(modulation: AudioModulation, value: number): readonly [number, number] {
  switch (modulation) {
    case 'PILOT':
    case 'BPSK':
      return value === 0 ? [-1, 0] : [1, 0];
    case 'QPSK': {
      // Reflected Gray mapping: the quadrant ring reads 00, 01, 11, 10, so a
      // nearest-neighbour symbol error across either decision axis costs one
      // bit instead of two. The 16QAM mapping below is Gray-coded per axis
      // for the same reason.
      const points = [
        [-0.707, -0.707],
        [-0.707, 0.707],
        [0.707, -0.707],
        [0.707, 0.707],
      ] as const;
      return points[value % points.length] ?? points[0];
    }
    case '16QAM': {
      const levels = [-0.949, -0.316, 0.949, 0.316] as const;
      return [levels[(value >>> 2) & 3] ?? 0, levels[value & 3] ?? 0];
    }
    case 'OFF':
      return [0, 0];
  }
}

function trainingConstellation(carrier: AudioCarrier): readonly [number, number] {
  // A deterministic quadrature pattern prevents the training field from
  // collapsing into a high-crest-factor in-phase multitone.
  const value = ((carrier.index * 13 + carrier.bin * 7) ^ 0x5a) & 3;
  return constellation('QPSK', value);
}

function renderOfdmSymbol(
  points: ReadonlyMap<number, readonly [number, number]>,
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
  absoluteSymbolStart = 0,
  carrierFrequencyOffsetHz = 0,
): Float32Array {
  const output = new Float32Array(summary.samplesPerSymbol);
  for (let outputSample = 0; outputSample < output.length; outputSample += 1) {
    const relativeSample = outputSample - plan.cyclicPrefixSamples;
    const nominalSample =
      relativeSample < 0 ? plan.fftSize + relativeSample : relativeSample;
    const absoluteSample = absoluteSymbolStart + outputSample;
    let value = 0;
    for (const carrier of summary.active) {
      const [real, imaginary] = points.get(carrier.index) ?? [0, 0];
      const phase =
        (2 * Math.PI * carrier.bin * nominalSample) / plan.fftSize +
        (2 * Math.PI * carrierFrequencyOffsetHz * absoluteSample) /
          plan.sampleRate;
      value += real * Math.cos(phase) - imaginary * Math.sin(phase);
    }
    output[outputSample] = value * summary.amplitudeScale;
  }
  return output;
}

function synthesiseSymbolFromBitstream(
  bytes: Uint8Array,
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
  startBit: number,
  absoluteSymbolStart = 0,
  carrierFrequencyOffsetHz = 0,
): Float32Array {
  const points = new Map<number, readonly [number, number]>();
  let bitOffset = startBit;
  for (const carrier of summary.active) {
    if (carrier.modulation === 'PILOT') {
      points.set(carrier.index, constellation('PILOT', 1));
      continue;
    }
    const symbol = bitsForCarrier(bytes, bitOffset, carrier.bitsPerSymbol);
    points.set(carrier.index, constellation(carrier.modulation, symbol));
    bitOffset += carrier.bitsPerSymbol;
  }
  return renderOfdmSymbol(
    points,
    plan,
    summary,
    absoluteSymbolStart,
    carrierFrequencyOffsetHz,
  );
}

export function synthesiseOfdmSymbol(
  bytes: Uint8Array,
  plan: AudioCarrierPlan,
): Float32Array {
  const summary = validatePlan(plan);
  if (bytes.length * 8 > summary.bitsPerSymbol) {
    throw new ProtocolError(
      'AUDIO_SYMBOL_CAPACITY',
      'Payload does not fit in one OFDM symbol without repetition.',
    );
  }
  return synthesiseSymbolFromBitstream(bytes, plan, summary, 0);
}

function encodeBurstFrame(payload: Uint8Array): Uint8Array {
  if (payload.length > 0xffff_ffff - AUDIO_BURST_HEADER_BYTES) {
    throw new ProtocolError(
      'AUDIO_BURST_BOUNDS',
      'Acoustic burst payload exceeds its frame limit.',
    );
  }
  const framed = new Uint8Array(AUDIO_BURST_HEADER_BYTES + payload.length);
  const view = new DataView(framed.buffer);
  view.setUint32(0, AUDIO_BURST_MAGIC);
  view.setUint8(4, AUDIO_BURST_VERSION);
  view.setUint8(5, 0);
  view.setUint16(6, AUDIO_BURST_HEADER_BYTES);
  view.setUint32(8, payload.length);
  view.setUint32(12, crc32c(payload));
  framed.set(payload, AUDIO_BURST_HEADER_BYTES);
  return framed;
}

interface AudioPreambleLayout {
  readonly samples: Float32Array;
  readonly sync: Float32Array;
  readonly firstTrainingOffset: number;
  readonly secondTrainingOffset: number;
  readonly burstOffset: number;
}

function preambleSampleCount(
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
): number {
  return (
    plan.fftSize +
    plan.cyclicPrefixSamples +
    summary.samplesPerSymbol * 2 +
    plan.cyclicPrefixSamples
  );
}

function generateSyncWaveform(
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
  carrierFrequencyOffsetHz: number,
): Float32Array {
  const output = new Float32Array(plan.fftSize);
  // The acquisition chirp sweeps only the band the plan occupies. Under
  // frequency-division duplex both directions transmit concurrently, and a
  // full-span chirp would land inside the opposite direction's band and
  // defeat the out-of-band self-interference argument exactly at preamble
  // acquisition. Deriving the sweep from the enabled carriers keeps the
  // synthesiser and the correlation template symmetric by construction.
  let minCarrierHz = Number.POSITIVE_INFINITY;
  let maxCarrierHz = 0;
  for (const carrier of summary.active) {
    minCarrierHz = Math.min(minCarrierHz, carrier.frequencyHz);
    maxCarrierHz = Math.max(maxCarrierHz, carrier.frequencyHz);
  }
  const startHz = Math.max(150, minCarrierHz * 0.88);
  const endHz = Math.max(startHz + 1, Math.min(plan.sampleRate * 0.42, maxCarrierHz * 1.06));
  const sweepRatio = endHz / startHz;
  let phase = 0;
  for (let sample = 0; sample < output.length; sample += 1) {
    const progress = sample / Math.max(1, output.length - 1);
    const frequency = startHz * sweepRatio ** progress + carrierFrequencyOffsetHz;
    phase += (2 * Math.PI * frequency) / plan.sampleRate;
    const window = Math.sin(Math.PI * progress) ** 2;
    output[sample] = Math.sin(phase) * window * AUDIO_SYNC_AMPLITUDE;
  }
  return output;
}

function buildAudioPreamble(
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
  carrierFrequencyOffsetHz: number,
): AudioPreambleLayout {
  const sync = generateSyncWaveform(plan, summary, carrierFrequencyOffsetHz);
  const leadingGuardSamples = plan.cyclicPrefixSamples;
  const firstTrainingOffset = sync.length + leadingGuardSamples;
  const secondTrainingOffset = firstTrainingOffset + summary.samplesPerSymbol;
  const burstOffset = preambleSampleCount(plan, summary);
  const samples = new Float32Array(burstOffset);
  samples.set(sync, 0);

  const points = new Map<number, readonly [number, number]>();
  for (const carrier of summary.active) {
    points.set(carrier.index, trainingConstellation(carrier));
  }
  samples.set(
    renderOfdmSymbol(
      points,
      plan,
      summary,
      firstTrainingOffset,
      carrierFrequencyOffsetHz,
    ),
    firstTrainingOffset,
  );
  samples.set(
    renderOfdmSymbol(
      points,
      plan,
      summary,
      secondTrainingOffset,
      carrierFrequencyOffsetHz,
    ),
    secondTrainingOffset,
  );

  return {
    samples,
    sync,
    firstTrainingOffset,
    secondTrainingOffset,
    burstOffset,
  };
}

function validateCarrierFrequencyOffset(value: number): void {
  if (!Number.isFinite(value) || Math.abs(value) > MAX_COMMON_CARRIER_OFFSET_HZ) {
    throw new ProtocolError(
      'AUDIO_FREQUENCY_OFFSET_BOUNDS',
      'Common carrier offset exceeds the bounded acquisition range.',
    );
  }
}

export function generateOfdmPreamble(plan: AudioCarrierPlan): Float32Array {
  const summary = validatePlan(plan);
  return buildAudioPreamble(plan, summary, 0).samples;
}

export function synthesiseOfdmBurst(
  payload: Uint8Array,
  plan: AudioCarrierPlan,
): SynthesisedAudioBurst {
  const summary = validatePlan(plan);
  const framed = encodeBurstFrame(payload);
  const symbolCount = Math.ceil((framed.length * 8) / summary.bitsPerSymbol);
  const sampleCount = symbolCount * summary.samplesPerSymbol;
  if (!Number.isSafeInteger(sampleCount) || sampleCount > MAX_AUDIO_BURST_SAMPLES) {
    throw new ProtocolError(
      'AUDIO_BURST_BOUNDS',
      'Acoustic burst would exceed the prototype PCM allocation limit.',
    );
  }

  const samples = new Float32Array(sampleCount);
  for (let symbol = 0; symbol < symbolCount; symbol += 1) {
    samples.set(
      synthesiseSymbolFromBitstream(
        framed,
        plan,
        summary,
        symbol * summary.bitsPerSymbol,
      ),
      symbol * summary.samplesPerSymbol,
    );
  }

  return {
    samples,
    symbolCount,
    bitsPerSymbol: summary.bitsPerSymbol,
    payloadBytes: payload.length,
    durationSeconds: samples.length / plan.sampleRate,
  };
}

function estimateOfdmTransmissionWithSummary(
  payloadBytes: number,
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
): AudioTransmissionEstimate {
  if (
    !Number.isSafeInteger(payloadBytes) ||
    payloadBytes < 0 ||
    payloadBytes > 0xffff_ffff - AUDIO_BURST_HEADER_BYTES
  ) {
    throw new ProtocolError(
      'AUDIO_BURST_BOUNDS',
      'Acoustic transmission payload length exceeds its framed bounds.',
    );
  }
  const framedBytes = AUDIO_BURST_HEADER_BYTES + payloadBytes;
  const symbolCount = Math.ceil((framedBytes * 8) / summary.bitsPerSymbol);
  const burstSamples = symbolCount * summary.samplesPerSymbol;
  const preambleSamples = preambleSampleCount(plan, summary);
  const totalSamples = preambleSamples + burstSamples;
  if (!Number.isSafeInteger(totalSamples) || totalSamples > MAX_AUDIO_BURST_SAMPLES) {
    throw new ProtocolError(
      'AUDIO_BURST_BOUNDS',
      'Acoustic transmission would exceed the prototype PCM allocation limit.',
    );
  }
  return {
    payloadBytes,
    framedBytes,
    bitsPerSymbol: summary.bitsPerSymbol,
    symbolCount,
    samplesPerSymbol: summary.samplesPerSymbol,
    symbolDurationSeconds: summary.samplesPerSymbol / plan.sampleRate,
    preambleSamples,
    preambleDurationSeconds: preambleSamples / plan.sampleRate,
    burstSamples,
    totalSamples,
    durationSeconds: totalSamples / plan.sampleRate,
  };
}

export function estimateOfdmTransmission(
  payloadBytes: number,
  plan: AudioCarrierPlan,
): AudioTransmissionEstimate {
  return estimateOfdmTransmissionWithSummary(payloadBytes, plan, validatePlan(plan));
}

export function synthesiseOfdmTransmission(
  payload: Uint8Array,
  plan: AudioCarrierPlan,
  options: AudioTransmissionOptions = {},
): SynthesisedAudioTransmission {
  const summary = validatePlan(plan);
  const carrierFrequencyOffsetHz = options.carrierFrequencyOffsetHz ?? 0;
  validateCarrierFrequencyOffset(carrierFrequencyOffsetHz);
  const preamble = buildAudioPreamble(
    plan,
    summary,
    carrierFrequencyOffsetHz,
  );
  const estimate = estimateOfdmTransmissionWithSummary(payload.length, plan, summary);
  const framed = encodeBurstFrame(payload);
  const symbolCount = estimate.symbolCount;
  const burstSamples = estimate.burstSamples;
  const sampleCount = estimate.totalSamples;

  const samples = new Float32Array(sampleCount);
  samples.set(preamble.samples, 0);
  for (let symbol = 0; symbol < symbolCount; symbol += 1) {
    const symbolStart = preamble.burstOffset + symbol * summary.samplesPerSymbol;
    samples.set(
      synthesiseSymbolFromBitstream(
        framed,
        plan,
        summary,
        symbol * summary.bitsPerSymbol,
        symbolStart,
        carrierFrequencyOffsetHz,
      ),
      symbolStart,
    );
  }

  return {
    samples,
    preambleSamples: preamble.burstOffset,
    burstSampleOffset: preamble.burstOffset,
    burstSamples,
    symbolCount,
    bitsPerSymbol: summary.bitsPerSymbol,
    payloadBytes: payload.length,
    durationSeconds: samples.length / plan.sampleRate,
    carrierFrequencyOffsetHz,
  };
}

function projectCarrier(
  samples: Float32Array,
  sampleOffset: number,
  plan: AudioCarrierPlan,
  carrier: AudioCarrier,
  carrierFrequencyOffsetHz = 0,
): readonly [number, number] {
  let real = 0;
  let imaginary = 0;
  const dataOffset = sampleOffset + plan.cyclicPrefixSamples;
  for (let sample = 0; sample < plan.fftSize; sample += 1) {
    const value = samples[dataOffset + sample] ?? 0;
    const phase =
      (2 * Math.PI * carrier.bin * sample) / plan.fftSize +
      (2 * Math.PI * carrierFrequencyOffsetHz * sample) / plan.sampleRate;
    real += value * Math.cos(phase);
    imaginary -= value * Math.sin(phase);
  }
  const normalisation = 2 / plan.fftSize;
  return [real * normalisation, imaginary * normalisation];
}

function nearestConstellationValue(
  modulation: AudioModulation,
  real: number,
  imaginary: number,
): number {
  const pointCount = 2 ** expectedBitsPerSymbol(modulation);
  let nearestValue = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let value = 0; value < pointCount; value += 1) {
    const [candidateReal, candidateImaginary] = constellation(modulation, value);
    const distance =
      (real - candidateReal) ** 2 + (imaginary - candidateImaginary) ** 2;
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestValue = value;
    }
  }
  return nearestValue;
}

/** Per-carrier complex channel estimates keyed by carrier index. */
type CarrierEqualiser = ReadonlyMap<number, readonly [number, number]>;

function complexDivide(
  numeratorReal: number,
  numeratorImaginary: number,
  denominatorReal: number,
  denominatorImaginary: number,
): readonly [number, number] {
  const power = denominatorReal ** 2 + denominatorImaginary ** 2;
  if (power < MIN_PILOT_GAIN ** 2) {
    // A notched carrier carries no usable decision; returning the origin
    // makes its bits noise and leaves arbitration to the burst CRC.
    return [0, 0];
  }
  return [
    (numeratorReal * denominatorReal + numeratorImaginary * denominatorImaginary) / power,
    (numeratorImaginary * denominatorReal - numeratorReal * denominatorImaginary) / power,
  ];
}

function decodeSymbolBits(
  samples: Float32Array,
  sampleOffset: number,
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
  carrierFrequencyOffsetHz = 0,
  equaliser?: CarrierEqualiser,
): readonly number[] {
  // A real loudspeaker-to-microphone channel is anything but flat: comb
  // filtering from room reflections and device response tilt swing tens of
  // decibels across the band. With per-carrier training estimates in hand,
  // each carrier is equalised against its own channel and the pilots only
  // track the residual common rotation that accumulates over the burst.
  // Without training (the raw burst decoder), the pilots fall back to the
  // historical common-gain estimate, which assumes a flat channel.
  let residualReal = 0;
  let residualImaginary = 0;
  for (const carrier of summary.pilots) {
    const [observedReal, observedImaginary] = projectCarrier(
      samples,
      sampleOffset,
      plan,
      carrier,
      carrierFrequencyOffsetHz,
    );
    if (equaliser === undefined) {
      residualReal += observedReal;
      residualImaginary += observedImaginary;
    } else {
      const channel = equaliser.get(carrier.index) ?? [0, 0];
      const [alignedReal, alignedImaginary] = complexDivide(
        observedReal,
        observedImaginary,
        channel[0] * summary.amplitudeScale,
        channel[1] * summary.amplitudeScale,
      );
      residualReal += alignedReal;
      residualImaginary += alignedImaginary;
    }
  }
  const gainReal = residualReal / summary.pilots.length;
  const gainImaginary = residualImaginary / summary.pilots.length;
  const gainPower = gainReal ** 2 + gainImaginary ** 2;
  if (gainPower < MIN_PILOT_GAIN ** 2) {
    throw new ProtocolError(
      'AUDIO_PILOT_LOST',
      'OFDM pilot energy is insufficient to demodulate the captured symbol.',
    );
  }

  const bits: number[] = [];
  for (const carrier of summary.data) {
    let [observedReal, observedImaginary] = projectCarrier(
      samples,
      sampleOffset,
      plan,
      carrier,
      carrierFrequencyOffsetHz,
    );
    if (equaliser !== undefined) {
      const channel = equaliser.get(carrier.index) ?? [0, 0];
      [observedReal, observedImaginary] = complexDivide(
        observedReal,
        observedImaginary,
        channel[0] * summary.amplitudeScale,
        channel[1] * summary.amplitudeScale,
      );
    }
    const real =
      (observedReal * gainReal + observedImaginary * gainImaginary) / gainPower;
    const imaginary =
      (observedImaginary * gainReal - observedReal * gainImaginary) / gainPower;
    const value = nearestConstellationValue(carrier.modulation, real, imaginary);
    for (let bit = carrier.bitsPerSymbol - 1; bit >= 0; bit -= 1) {
      bits.push((value >>> bit) & 1);
    }
  }
  return bits;
}

function bitsToBytes(bits: readonly number[]): Uint8Array {
  const bytes = new Uint8Array(Math.floor(bits.length / 8));
  for (let index = 0; index < bytes.length * 8; index += 1) {
    const byteIndex = Math.floor(index / 8);
    bytes[byteIndex] =
      (bytes[byteIndex] ?? 0) | ((bits[index] ?? 0) << (7 - (index % 8)));
  }
  return bytes;
}

interface ParsedAudioBurstHeader {
  readonly payloadLength: number;
  readonly payloadCrc32c: number;
  readonly symbolCount: number;
}

function validatePcm(samples: Float32Array): void {
  for (const sample of samples) {
    if (!Number.isFinite(sample)) {
      throw new ProtocolError(
        'AUDIO_PCM_INVALID',
        'Captured PCM contains a non-finite sample.',
      );
    }
  }
}

function decodeCapturedBits(
  samples: Float32Array,
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
  symbolCount: number,
  carrierFrequencyOffsetHz: number,
  equaliser?: CarrierEqualiser,
): number[] {
  const bits: number[] = [];
  for (let symbol = 0; symbol < symbolCount; symbol += 1) {
    bits.push(
      ...decodeSymbolBits(
        samples,
        symbol * summary.samplesPerSymbol,
        plan,
        summary,
        carrierFrequencyOffsetHz,
        equaliser,
      ),
    );
  }
  return bits;
}

function parseAudioBurstHeader(
  framed: Uint8Array,
  summary: ValidatedAudioPlan,
): ParsedAudioBurstHeader {
  if (framed.length < AUDIO_BURST_HEADER_BYTES) {
    throw new ProtocolError(
      'AUDIO_BURST_TRUNCATED',
      'Captured OFDM symbols do not contain the complete acoustic header.',
    );
  }

  const view = new DataView(framed.buffer, framed.byteOffset, framed.byteLength);
  if (view.getUint32(0) !== AUDIO_BURST_MAGIC) {
    throw new ProtocolError('AUDIO_BURST_MAGIC', 'Acoustic burst magic is invalid.');
  }
  if (view.getUint8(4) !== AUDIO_BURST_VERSION) {
    throw new ProtocolError(
      'AUDIO_BURST_VERSION',
      'Acoustic burst version is unsupported.',
    );
  }
  if (view.getUint8(5) !== 0 || view.getUint16(6) !== AUDIO_BURST_HEADER_BYTES) {
    throw new ProtocolError('AUDIO_BURST_HEADER', 'Acoustic burst header is malformed.');
  }

  const payloadLength = view.getUint32(8);
  const payloadCrc32c = view.getUint32(12);
  const requiredBits = (AUDIO_BURST_HEADER_BYTES + payloadLength) * 8;
  const symbolCount = Math.ceil(requiredBits / summary.bitsPerSymbol);
  const requiredSamples = symbolCount * summary.samplesPerSymbol;
  if (
    !Number.isSafeInteger(requiredSamples) ||
    requiredSamples > MAX_AUDIO_BURST_SAMPLES
  ) {
    throw new ProtocolError(
      'AUDIO_BURST_BOUNDS',
      'Declared acoustic payload exceeds the prototype PCM allocation limit.',
    );
  }
  return { payloadLength, payloadCrc32c, symbolCount };
}

function demodulateOfdmBurstWithOffset(
  samples: Float32Array,
  plan: AudioCarrierPlan,
  carrierFrequencyOffsetHz: number,
  equaliser?: CarrierEqualiser,
): DemodulatedAudioBurst {
  const summary = validatePlan(plan);
  if (samples.length < summary.samplesPerSymbol) {
    throw new ProtocolError(
      'AUDIO_BURST_TRUNCATED',
      'Captured PCM does not contain one complete OFDM symbol.',
    );
  }
  if (samples.length % summary.samplesPerSymbol !== 0) {
    throw new ProtocolError(
      'AUDIO_BURST_ALIGNMENT',
      'Captured PCM ends inside an OFDM symbol.',
    );
  }
  validatePcm(samples);

  const capturedSymbolCount = samples.length / summary.samplesPerSymbol;
  const headerSymbolCount = Math.ceil(
    (AUDIO_BURST_HEADER_BYTES * 8) / summary.bitsPerSymbol,
  );
  if (capturedSymbolCount < headerSymbolCount) {
    throw new ProtocolError(
      'AUDIO_BURST_TRUNCATED',
      'Captured OFDM symbols do not contain the complete acoustic header.',
    );
  }
  const headerBits = decodeCapturedBits(
    samples,
    plan,
    summary,
    headerSymbolCount,
    carrierFrequencyOffsetHz,
    equaliser,
  );
  const header = parseAudioBurstHeader(bitsToBytes(headerBits), summary);
  if (header.symbolCount > capturedSymbolCount) {
    throw new ProtocolError(
      'AUDIO_BURST_TRUNCATED',
      'Captured OFDM burst is shorter than its declared payload.',
    );
  }

  const bits = decodeCapturedBits(
    samples,
    plan,
    summary,
    header.symbolCount,
    carrierFrequencyOffsetHz,
    equaliser,
  );
  const framed = bitsToBytes(bits);

  const payload = framed.slice(
    AUDIO_BURST_HEADER_BYTES,
    AUDIO_BURST_HEADER_BYTES + header.payloadLength,
  );
  if (crc32c(payload) !== header.payloadCrc32c) {
    throw new ProtocolError(
      'AUDIO_BURST_CRC',
      'Acoustic burst CRC32C verification failed.',
    );
  }

  return {
    payload,
    symbolCount: header.symbolCount,
    capturedSymbolCount,
    bitsPerSymbol: summary.bitsPerSymbol,
    payloadCrc32c: header.payloadCrc32c,
  };
}

export function demodulateOfdmBurst(
  samples: Float32Array,
  plan: AudioCarrierPlan,
): DemodulatedAudioBurst {
  return demodulateOfdmBurstWithOffset(samples, plan, 0);
}

interface PreambleAcquisition {
  readonly offset: number;
  readonly correlation: number;
}

interface ResolvedPreambleSearchOptions {
  readonly minimumCorrelation: number;
  readonly maximumSearchOffset: number;
}

interface PreambleSearchScan {
  readonly acquisition: PreambleAcquisition | null;
  readonly searchedOffsets: number;
}

function resolvePreambleSearchOptions(
  plan: AudioCarrierPlan,
  options: AudioReceiveOptions,
): ResolvedPreambleSearchOptions {
  const minimumCorrelation =
    options.minimumCorrelation ?? DEFAULT_PREAMBLE_CORRELATION;
  if (
    !Number.isFinite(minimumCorrelation) ||
    minimumCorrelation <= 0 ||
    minimumCorrelation > 1
  ) {
    throw new ProtocolError(
      'AUDIO_CORRELATION_BOUNDS',
      'Preamble correlation threshold must be in the interval (0, 1].',
    );
  }
  const maximumSearchOffset =
    options.maximumPreambleSearchSamples ?? plan.sampleRate * 3;
  if (!Number.isSafeInteger(maximumSearchOffset) || maximumSearchOffset < 0) {
    throw new ProtocolError(
      'AUDIO_SEARCH_BOUNDS',
      'Preamble search window must be a non-negative sample count.',
    );
  }
  return { minimumCorrelation, maximumSearchOffset };
}

function calculateSignalPower(samples: Float32Array): number {
  let power = 0;
  for (const value of samples) {
    power += value * value;
  }
  return power;
}

function scanPreambleOffsets(
  samples: Float32Array,
  sync: Float32Array,
  search: ResolvedPreambleSearchOptions,
  templatePower: number,
  confirmationSamples: number | null,
): PreambleSearchScan {
  if (samples.length < sync.length) {
    return { acquisition: null, searchedOffsets: 0 };
  }
  const completeMaximumOffset = samples.length - sync.length;
  const confirmedMaximumOffset = Math.min(
    search.maximumSearchOffset,
    completeMaximumOffset - (confirmationSamples ?? 0),
  );
  if (confirmedMaximumOffset < 0) {
    return { acquisition: null, searchedOffsets: 0 };
  }
  let windowPower = 0;
  for (let index = 0; index < sync.length; index += 1) {
    const value = samples[index] ?? 0;
    windowPower += value * value;
  }

  let bestOffset = -1;
  let bestCorrelation = 0;
  let matchingStarted = false;
  let scanMaximumOffset = confirmedMaximumOffset;
  for (let offset = 0; offset <= scanMaximumOffset; offset += 1) {
    let dotProduct = 0;
    for (let index = 0; index < sync.length; index += 1) {
      dotProduct += (samples[offset + index] ?? 0) * (sync[index] ?? 0);
    }
    const denominator = Math.sqrt(templatePower * windowPower);
    const correlation = denominator > 0 ? Math.abs(dotProduct) / denominator : 0;
    if (correlation > bestCorrelation) {
      bestCorrelation = correlation;
      bestOffset = offset;
    }
    if (
      confirmationSamples !== null &&
      !matchingStarted &&
      correlation >= search.minimumCorrelation
    ) {
      matchingStarted = true;
      // A bounded forward guard selects the matched-filter peak rather than
      // the first threshold crossing on the sync waveform's leading lobe.
      scanMaximumOffset = Math.min(
        completeMaximumOffset,
        offset + confirmationSamples,
      );
    }

    const outgoing = samples[offset] ?? 0;
    const incoming = samples[offset + sync.length] ?? 0;
    windowPower = Math.max(
      0,
      windowPower - outgoing * outgoing + incoming * incoming,
    );
  }

  return {
    acquisition:
      bestOffset >= 0 && bestCorrelation >= search.minimumCorrelation
        ? { offset: bestOffset, correlation: Math.min(1, bestCorrelation) }
        : null,
    searchedOffsets: scanMaximumOffset + 1,
  };
}

function acquirePreamble(
  samples: Float32Array,
  sync: Float32Array,
  plan: AudioCarrierPlan,
  options: AudioReceiveOptions,
): PreambleAcquisition {
  const search = resolvePreambleSearchOptions(plan, options);
  if (samples.length < sync.length) {
    throw new ProtocolError(
      'AUDIO_RECEIVE_INCOMPLETE',
      'Captured PCM does not yet contain the complete acoustic sync word.',
    );
  }
  const scan = scanPreambleOffsets(
    samples,
    sync,
    search,
    calculateSignalPower(sync),
    null,
  );
  if (scan.acquisition === null) {
    throw new ProtocolError(
      'AUDIO_PREAMBLE_NOT_FOUND',
      'No acoustic preamble exceeded the configured correlation threshold.',
    );
  }
  return scan.acquisition;
}

interface TrainingEstimate {
  readonly gain: number;
  readonly commonCarrierOffsetHz: number;
  readonly commonPhaseRadians: number;
  readonly snrDb: number;
  readonly carrierGains: CarrierEqualiser;
}

function divideByTrainingPoint(
  observed: readonly [number, number],
  expected: readonly [number, number],
  amplitudeScale: number,
): readonly [number, number] {
  const denominator =
    amplitudeScale * (expected[0] ** 2 + expected[1] ** 2);
  return [
    (observed[0] * expected[0] + observed[1] * expected[1]) / denominator,
    (observed[1] * expected[0] - observed[0] * expected[1]) / denominator,
  ];
}

function estimateTrainingChannel(
  samples: Float32Array,
  preambleStart: number,
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
  layout: AudioPreambleLayout,
): TrainingEstimate {
  const firstChannels: Array<readonly [number, number]> = [];
  const secondChannels: Array<readonly [number, number]> = [];
  let crossReal = 0;
  let crossImaginary = 0;
  for (const carrier of summary.active) {
    const expected = trainingConstellation(carrier);
    const first = divideByTrainingPoint(
      projectCarrier(
        samples,
        preambleStart + layout.firstTrainingOffset,
        plan,
        carrier,
      ),
      expected,
      summary.amplitudeScale,
    );
    const second = divideByTrainingPoint(
      projectCarrier(
        samples,
        preambleStart + layout.secondTrainingOffset,
        plan,
        carrier,
      ),
      expected,
      summary.amplitudeScale,
    );
    firstChannels.push(first);
    secondChannels.push(second);
    crossReal += second[0] * first[0] + second[1] * first[1];
    crossImaginary += second[1] * first[0] - second[0] * first[1];
  }

  const phaseAdvance = Math.atan2(crossImaginary, crossReal);
  const commonCarrierOffsetHz =
    (phaseAdvance * plan.sampleRate) / (2 * Math.PI * summary.samplesPerSymbol);
  if (
    !Number.isFinite(commonCarrierOffsetHz) ||
    Math.abs(commonCarrierOffsetHz) > MAX_COMMON_CARRIER_OFFSET_HZ
  ) {
    throw new ProtocolError(
      'AUDIO_FREQUENCY_OFFSET_UNRESOLVED',
      'Repeated acoustic training symbols imply an unsupported carrier offset.',
    );
  }

  const rotationReal = Math.cos(-phaseAdvance);
  const rotationImaginary = Math.sin(-phaseAdvance);
  let signalPower = 0;
  let errorPower = 0;
  let phaseReal = 0;
  let phaseImaginary = 0;
  // The two training symbols yield an independent complex channel estimate
  // per carrier. Averaging them (after CFO alignment) halves the estimator
  // noise and preserves the frequency-selective shape a real acoustic
  // channel always has — the information the flat common-gain summary
  // below deliberately discards.
  const carrierGains = new Map<number, readonly [number, number]>();
  for (let index = 0; index < firstChannels.length; index += 1) {
    const first = firstChannels[index] ?? [0, 0];
    const second = secondChannels[index] ?? [0, 0];
    const alignedSecondReal =
      second[0] * rotationReal - second[1] * rotationImaginary;
    const alignedSecondImaginary =
      second[0] * rotationImaginary + second[1] * rotationReal;
    signalPower +=
      (first[0] ** 2 + first[1] ** 2 +
        alignedSecondReal ** 2 + alignedSecondImaginary ** 2) /
      2;
    errorPower +=
      ((alignedSecondReal - first[0]) ** 2 +
        (alignedSecondImaginary - first[1]) ** 2) /
      2;
    phaseReal += first[0];
    phaseImaginary += first[1];
    const carrier = summary.active[index];
    if (carrier !== undefined) {
      carrierGains.set(carrier.index, [
        (first[0] + alignedSecondReal) / 2,
        (first[1] + alignedSecondImaginary) / 2,
      ]);
    }
  }
  signalPower /= Math.max(1, firstChannels.length);
  errorPower /= Math.max(1, firstChannels.length);
  if (signalPower < MIN_PILOT_GAIN ** 2) {
    throw new ProtocolError(
      'AUDIO_TRAINING_LOST',
      'Acoustic training energy is insufficient for channel estimation.',
    );
  }
  const snrDb = Math.max(
    -20,
    Math.min(120, 10 * Math.log10(signalPower / Math.max(errorPower, 1e-12))),
  );
  return {
    gain: Math.sqrt(signalPower),
    commonCarrierOffsetHz,
    commonPhaseRadians: Math.atan2(phaseImaginary, phaseReal),
    snrDb,
    carrierGains,
  };
}

function receiveAcquiredOfdmTransmission(
  samples: Float32Array,
  plan: AudioCarrierPlan,
  summary: ValidatedAudioPlan,
  reference: AudioPreambleLayout,
  acquisition: PreambleAcquisition,
): ReceivedAudioTransmission {
  const preambleEnd = acquisition.offset + reference.burstOffset;
  if (samples.length < preambleEnd) {
    throw new ProtocolError(
      'AUDIO_RECEIVE_INCOMPLETE',
      'Captured PCM ends inside the acoustic training field.',
    );
  }
  const channel = estimateTrainingChannel(
    samples,
    acquisition.offset,
    plan,
    summary,
    reference,
  );

  const headerSymbolCount = Math.ceil(
    (AUDIO_BURST_HEADER_BYTES * 8) / summary.bitsPerSymbol,
  );
  const headerSamples = headerSymbolCount * summary.samplesPerSymbol;
  if (samples.length < preambleEnd + headerSamples) {
    throw new ProtocolError(
      'AUDIO_RECEIVE_INCOMPLETE',
      'Captured PCM does not yet contain the complete acoustic header.',
    );
  }
  const headerPcm = samples.subarray(preambleEnd, preambleEnd + headerSamples);
  const headerBits = decodeCapturedBits(
    headerPcm,
    plan,
    summary,
    headerSymbolCount,
    channel.commonCarrierOffsetHz,
    channel.carrierGains,
  );
  const header = parseAudioBurstHeader(bitsToBytes(headerBits), summary);
  const burstSamples = header.symbolCount * summary.samplesPerSymbol;
  const burstEnd = preambleEnd + burstSamples;
  if (samples.length < burstEnd) {
    throw new ProtocolError(
      'AUDIO_RECEIVE_INCOMPLETE',
      'Captured PCM ends before the declared acoustic payload is complete.',
    );
  }

  const decoded = demodulateOfdmBurstWithOffset(
    samples.slice(preambleEnd, burstEnd),
    plan,
    channel.commonCarrierOffsetHz,
    channel.carrierGains,
  );
  return {
    ...decoded,
    diagnostics: {
      source: 'measured',
      correlation: acquisition.correlation,
      gain: channel.gain,
      commonCarrierOffsetHz: channel.commonCarrierOffsetHz,
      commonPhaseRadians: channel.commonPhaseRadians,
      snrDb: channel.snrDb,
      sampleWindow: {
        captureSamples: samples.length,
        preambleStart: acquisition.offset,
        preambleEnd,
        burstStart: preambleEnd,
        burstEnd,
      },
    },
  };
}

export function receiveOfdmTransmission(
  samples: Float32Array,
  plan: AudioCarrierPlan,
  options: AudioReceiveOptions = {},
): ReceivedAudioTransmission {
  const summary = validatePlan(plan);
  if (samples.length > MAX_AUDIO_STREAM_SAMPLES) {
    throw new ProtocolError(
      'AUDIO_CAPTURE_BOUNDS',
      'Captured PCM exceeds the bounded receiver allocation.',
    );
  }
  validatePcm(samples);
  const reference = buildAudioPreamble(plan, summary, 0);
  const acquisition = acquirePreamble(samples, reference.sync, plan, options);
  return receiveAcquiredOfdmTransmission(
    samples,
    plan,
    summary,
    reference,
    acquisition,
  );
}

function withAbsoluteSampleWindow(
  received: ReceivedAudioTransmission,
  absoluteOffset: number,
  capturedSamples: number,
): ReceivedAudioTransmission {
  const window = received.diagnostics.sampleWindow;
  return {
    ...received,
    diagnostics: {
      ...received.diagnostics,
      sampleWindow: {
        captureSamples: capturedSamples,
        preambleStart: absoluteOffset + window.preambleStart,
        preambleEnd: absoluteOffset + window.preambleEnd,
        burstStart: absoluteOffset + window.burstStart,
        burstEnd: absoluteOffset + window.burstEnd,
      },
    },
  };
}

export class OfdmBurstStreamReceiver {
  readonly #plan: AudioCarrierPlan;
  readonly #summary: ValidatedAudioPlan;
  readonly #reference: AudioPreambleLayout;
  readonly #search: ResolvedPreambleSearchOptions;
  readonly #syncPower: number;
  readonly #maximumBufferedSamples: number;
  #samples = new Float32Array(0);
  #acquisition: PreambleAcquisition | null = null;
  #absoluteSampleOffset = 0;
  #receivedSampleCount = 0;
  #searchedPreambleOffsets = 0;
  #preambleCorrelationComparisons = 0;

  public constructor(
    plan: AudioCarrierPlan,
    options: AudioReceiveOptions = {},
    maximumBufferedSamples = MAX_AUDIO_STREAM_SAMPLES,
  ) {
    const summary = validatePlan(plan);
    const reference = buildAudioPreamble(plan, summary, 0);
    if (
      !Number.isInteger(maximumBufferedSamples) ||
      maximumBufferedSamples < reference.samples.length ||
      maximumBufferedSamples > MAX_AUDIO_STREAM_SAMPLES
    ) {
      throw new ProtocolError(
        'AUDIO_STREAM_BOUNDS',
        'Streaming receiver buffer limit is outside supported bounds.',
      );
    }
    this.#plan = plan;
    this.#summary = summary;
    this.#reference = reference;
    this.#search = resolvePreambleSearchOptions(plan, options);
    this.#syncPower = calculateSignalPower(reference.sync);
    this.#maximumBufferedSamples = maximumBufferedSamples;
  }

  public get bufferedSamples(): number {
    return this.#samples.length;
  }

  public get searchedPreambleOffsets(): number {
    return this.#searchedPreambleOffsets;
  }

  public get preambleCorrelationComparisons(): number {
    return this.#preambleCorrelationComparisons;
  }

  public push(chunk: Float32Array): ReceivedAudioTransmission | null {
    validatePcm(chunk);
    if (this.#samples.length + chunk.length > this.#maximumBufferedSamples) {
      throw new ProtocolError(
        'AUDIO_STREAM_BOUNDS',
        'Streaming receiver buffer exceeded its configured allocation.',
      );
    }
    const combined = new Float32Array(this.#samples.length + chunk.length);
    combined.set(this.#samples, 0);
    combined.set(chunk, this.#samples.length);
    this.#samples = combined;
    this.#receivedSampleCount += chunk.length;

    try {
      this.#seekPreamble(false);
      if (this.#acquisition === null) {
        return null;
      }
      const local = receiveAcquiredOfdmTransmission(
        this.#samples,
        this.#plan,
        this.#summary,
        this.#reference,
        this.#acquisition,
      );
      const consumedSamples = local.diagnostics.sampleWindow.burstEnd;
      const received = withAbsoluteSampleWindow(
        local,
        this.#absoluteSampleOffset,
        this.#receivedSampleCount,
      );
      this.#samples = this.#samples.slice(consumedSamples);
      this.#absoluteSampleOffset += consumedSamples;
      this.#acquisition = null;
      return received;
    } catch (error) {
      if (
        error instanceof ProtocolError &&
        error.code === 'AUDIO_RECEIVE_INCOMPLETE'
      ) {
        return null;
      }
      throw error;
    }
  }

  public finish(): ReceivedAudioTransmission {
    this.#seekPreamble(true);
    if (this.#acquisition === null) {
      if (this.#receivedSampleCount < this.#reference.sync.length) {
        throw new ProtocolError(
          'AUDIO_RECEIVE_INCOMPLETE',
          'Captured PCM does not yet contain the complete acoustic sync word.',
        );
      }
      throw new ProtocolError(
        'AUDIO_PREAMBLE_NOT_FOUND',
        'No acoustic preamble exceeded the configured correlation threshold.',
      );
    }
    const local = receiveAcquiredOfdmTransmission(
      this.#samples,
      this.#plan,
      this.#summary,
      this.#reference,
      this.#acquisition,
    );
    return withAbsoluteSampleWindow(
      local,
      this.#absoluteSampleOffset,
      this.#receivedSampleCount,
    );
  }

  public reset(): void {
    this.#samples = new Float32Array(0);
    this.#acquisition = null;
    this.#absoluteSampleOffset = 0;
    this.#receivedSampleCount = 0;
    this.#searchedPreambleOffsets = 0;
    this.#preambleCorrelationComparisons = 0;
  }

  #seekPreamble(final: boolean): void {
    while (
      this.#acquisition === null &&
      this.#samples.length >= this.#reference.sync.length
    ) {
      const scan = scanPreambleOffsets(
        this.#samples,
        this.#reference.sync,
        this.#search,
        this.#syncPower,
        final ? null : this.#reference.sync.length - 1,
      );
      if (scan.searchedOffsets === 0) {
        return;
      }
      this.#searchedPreambleOffsets += scan.searchedOffsets;
      this.#preambleCorrelationComparisons +=
        scan.searchedOffsets * this.#reference.sync.length;

      if (scan.acquisition !== null) {
        const preambleOffset = scan.acquisition.offset;
        if (preambleOffset > 0) {
          this.#samples = this.#samples.slice(preambleOffset);
          this.#absoluteSampleOffset += preambleOffset;
        }
        this.#acquisition = {
          offset: 0,
          correlation: scan.acquisition.correlation,
        };
        return;
      }

      // Every discarded offset has already been searched. The bounded tail
      // retains both sync overlap and the forward peak-confirmation guard.
      this.#samples = this.#samples.slice(scan.searchedOffsets);
      this.#absoluteSampleOffset += scan.searchedOffsets;
    }
  }
}
